import { Trans, useLingui } from '@lingui/react';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { useShareIssuesSuspenseQuery } from './ShareIssues.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { MeetingStatusHuman } from '../meetings/components/MeetingStatusHuman';
import { CreateShareIssueForm } from './CreateShareIssue';
import { Modal } from '@startuptools/ui/joy/Modal';
import { ModalDialog } from '@startuptools/ui/joy/ModalDialog';
import { DialogTitle } from '@startuptools/ui/Dialog';
import { ModalClose } from '@startuptools/ui/joy/ModalClose';
import { Suspense, useState } from 'react';
import { SiDeleteDialog } from './DeleteDialog';
import { GqlShareIssue } from '../../graphql/base-types.graphql';
import { Route, Routes } from 'react-router-dom';
import { ShareIssueRoutes } from './ShareIssue';
import { ItemList } from '@startuptools/ui/ItemList';
import { siLink } from '../../components/Links';
import { useNavigate } from '../../components/Navigate';

export const ShareIssuesRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<ShareIssues />} />
      <Route path=":shareIssueId" element={<ShareIssueRoutes />}>
        <Route path="*" element={<ShareIssueRoutes />} />
      </Route>
    </Routes>
  );
};

const ShareIssues = () => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const { link } = useNavigate();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<Pick<GqlShareIssue, 'id' | 'stripeInvoiceId'> | null>(null);
  const {
    data: { shareIssues },
  } = useShareIssuesSuspenseQuery({ variables: { companyId }, fetchPolicy: 'cache-and-network' });

  return (
    <Stack gap={3}>
      {showCreateDialog && (
        <Modal open onClose={() => setShowCreateDialog(false)}>
          <ModalDialog>
            <ModalClose />
            <Suspense>
              <DialogTitle>
                <Trans id="Create share issue" />
              </DialogTitle>
              <CreateShareIssueForm onClose={() => setShowCreateDialog(false)} />
            </Suspense>
          </ModalDialog>
        </Modal>
      )}

      <Stack direction="row" justifyContent="space-between">
        <Typography level="h2">
          <Trans id="Share Issues" />
        </Typography>
      </Stack>
      <ItemList
        globalActions={[
          {
            label: <Trans id="New Share Issue" />,
            onClick: () => setShowCreateDialog(true),
            buttonProps: { color: 'primary' },
          },
        ]}
        items={shareIssues.map(m => ({
          ...m,
          deletable: true,
        }))}
        linkTo={m => link(siLink(m))}
        onDelete={si => setShowDeleteDialog(si)}
        columns={[
          {
            header: <Trans id="Description" />,
            render: si => si.description,
          },
          {
            header: <Trans id="Decision" />,
            render: si => (
              <>{si.meetingConcern && <MeetingStatusHuman>{si.meetingConcern.meeting.status}</MeetingStatusHuman>}</>
            ),
          },
          {
            header: <Trans id="Last Day to Subscribe" />,
            render: si => <DateFormat locale={i18n.locale}>{si.subscriptionEndDate}</DateFormat>,
          },
          {
            header: <Trans id="Paid / Waiting / Not Paid" />,
            render: si => (
              <Stack direction="row">
                <Typography color="success">{si.paymentCount.paid}</Typography>
                {' / '}
                <Typography color="neutral">{si.paymentCount.waiting}</Typography>
                {' / '}
                <Typography color="danger">{si.paymentCount.notPaid}</Typography>
              </Stack>
            ),
          },
          {
            header: <Trans id="Maximum Number of Shares" />,
            render: si => (
              <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                {si.maxNumberOfShares}
              </NumberFormat>
            ),
            align: 'right',
          },
          {
            header: <Trans id="Total Amount" />,
            render: si => (
              <NumberFormat locale={i18n.locale} variant="currency" currency={si.currency}>
                {si.maxPaidAmount}
              </NumberFormat>
            ),
            align: 'right',
          },
        ]}
      />
      {showDeleteDialog && <SiDeleteDialog shareIssue={showDeleteDialog} onClose={() => setShowDeleteDialog(null)} />}
    </Stack>
  );
};
